import { useCallback, useEffect, useMemo } from 'react';
/* eslint-disable react-hooks/exhaustive-deps */
export default function useAdminEventChatListState(context) {
  const {
    ADMIN_SEARCH_API,
    API_URL,
    EVENTS_PAGE_LIMIT,
    EVENT_ASSIGNEE_SEARCH_STORAGE_KEY,
    applyEventTargetSearchDraft,
    applySearchResultEvents,
    assigneeSearch,
    buildToolbarFilterChips,
    clearEventTargetSearch,
    composeAdminEventRowWithDetail,
    countActiveEventSearchFilters,
    dlog,
    eventListForceFallbackExhaustedRef,
    eventListLoadMorePendingRef,
    eventListNode,
    events,
    fetchEvents,
    fetchJsonSafe,
    formatSearchChipDate,
    getEventDetail,
    hasMoreEvents,
    info,
    isAdminAssignedToEvent,
    isBootLoading,
    isLoadingEvents,
    isSearchLoadingMore,
    isSearchMode,
    isSearching,
    loadEventSearchRowsWithUnread,
    loadMoreEventList,
    loadMoreNode,
    messageSearchQuery,
    observeEventListLoadMoreScroll,
    observeEventListLoadMoreSentinel,
    openEventTargetSearchModal,
    orderFrozen,
    refreshEventChatUnread,
    refreshTopTimerRef,
    resolveEventSearchCriteria,
    scheduleEventListRefreshTop,
    searchDraft,
    searchEndDate,
    searchHasMore,
    searchNextOffset,
    searchQuery,
    searchSnapshotToken,
    searchStartDate,
    setAssigneeSearch,
    setEvents,
    setEventsOffset,
    setHasMoreEvents,
    setIsSearchLoadingMore,
    setIsSearchMode,
    setIsSearching,
    setMessageSearchQuery,
    setSearchDraft,
    setSearchEndDate,
    setSearchHasMore,
    setSearchModalError,
    setSearchNextOffset,
    setSearchQuery,
    setSearchSnapshotToken,
    setSearchStartDate,
    setShowSearchModal,
    shouldLoadMoreEventList,
    toastError,
    triggerEventListLoadMoreIfNeeded,
  } = context;
    const activeSearchFilterCount = useMemo(
      () =>
        countActiveEventSearchFilters({
          eventQuery: searchQuery,
          messageQuery: messageSearchQuery,
          startDate: searchStartDate,
          endDate: searchEndDate,
        }) + (assigneeSearch ? 1 : 0),
      [assigneeSearch, messageSearchQuery, searchEndDate, searchQuery, searchStartDate]
    );
    const searchSummaryLabel =
      activeSearchFilterCount > 0
        ? `絞り込み中: ${activeSearchFilterCount}件`
        : '';
    const assigneeQuickToggles = useMemo(
      () => [
        {
          key: 'mine',
          label: '担当',
          active: assigneeSearch === 'mine',
          onClick: () => setAssigneeSearch((prev) => (prev === 'mine' ? '' : 'mine')),
        },
      ],
      [assigneeSearch]
    );
    const displayedEvents = useMemo(
      () =>
        assigneeSearch === 'mine'
          ? events.filter((evt) => isAdminAssignedToEvent(evt))
          : events,
      [assigneeSearch, events, isAdminAssignedToEvent]
    );
    const isTargetFilterMode = isSearchMode || Boolean(assigneeSearch);

    const openTargetSearchModal = useCallback(() => {
      openEventTargetSearchModal({
        setErrorMessage: setSearchModalError,
        setSearchDraft,
        setShowSearchModal,
        currentSearch: {
          eventQuery: searchQuery,
          messageQuery: messageSearchQuery,
          startDate: searchStartDate,
          endDate: searchEndDate,
        },
      });
    }, [messageSearchQuery, searchEndDate, searchQuery, searchStartDate]);

    useEffect(() => {
      try {
        const saved = window.localStorage.getItem(EVENT_ASSIGNEE_SEARCH_STORAGE_KEY);
        if (saved === 'mine') setAssigneeSearch('mine');
      } catch {}
    }, []);

    useEffect(() => {
      try {
        if (assigneeSearch) {
          window.localStorage.setItem(EVENT_ASSIGNEE_SEARCH_STORAGE_KEY, assigneeSearch);
        } else {
          window.localStorage.removeItem(EVENT_ASSIGNEE_SEARCH_STORAGE_KEY);
        }
      } catch {}
    }, [assigneeSearch]);

    const clearSearch = useCallback(async () => {
      setAssigneeSearch('');
      setSearchHasMore(false);
      setSearchNextOffset(0);
      setSearchSnapshotToken('');
      setIsSearchLoadingMore(false);
      await clearEventTargetSearch({
        setErrorMessage: setSearchModalError,
        setIsSearchMode,
        setSearchQuery,
        setMessageSearchQuery,
        setSearchStartDate,
        setSearchEndDate,
        setHasMoreEvents,
        setEventsOffset,
        reloadDefaultList: () => fetchEvents('reset'),
        onCleared: () => info('検索条件をリセットしました'),
      });
    }, [fetchEvents, info]);

    /* 検索 */
    const performSearch = useCallback(async (override = null, { announceResult = true, append = false } = {}) => {
      const criteria = resolveEventSearchCriteria({
        override,
        eventQuery: searchQuery,
        messageQuery: messageSearchQuery,
        startDate: searchStartDate,
        endDate: searchEndDate,
      });
      if (!criteria.hasAny) {
        if (isSearchMode) {
          await clearSearch();
        } else {
          setSearchModalError('');
          setIsSearchMode(false);
        }
        return true;
      }
      try {
        setSearchModalError('');
        if (!append) setIsSearching(true);
        setIsSearchMode(true);

        const {
          rawEvents,
          unreadMap,
          hasMore: nextHasMore,
          nextOffset,
          snapshotToken,
        } = await loadEventSearchRowsWithUnread({
          searchUrl: ADMIN_SEARCH_API({
            q: criteria.q,
            message: criteria.message,
            startDate: criteria.startDate,
            endDate: criteria.endDate,
            offset: append ? searchNextOffset : 0,
            snapshotToken: append ? searchSnapshotToken : '',
          }),
          fetchJsonSafe,
          searchErrorLabel: 'イベント検索に失敗しました',
          apiUrl: API_URL,
          role: 'admin',
          unreadLabel: '',
        });

        const patched = await Promise.all(
          rawEvents.map(async evt => {
            try {
              const detail = await getEventDetail(evt.event_uuid);
              if (!detail) {
                return composeAdminEventRowWithDetail({
                  eventRow: evt,
                  detail: null,
                  unreadCount: unreadMap[evt.event_uuid] || 0,
                });
              }

              let originalDetail = null;
              if (
                detail.performance_flag === 'additional' &&
                detail.use_existing_flyers &&
                detail.original_event_uuid
              ) {
                originalDetail = await getEventDetail(detail.original_event_uuid);
              }
              return composeAdminEventRowWithDetail({
                eventRow: evt,
                detail,
                originalDetail,
                unreadCount: unreadMap[evt.event_uuid] || 0,
              });
            } catch {
              return composeAdminEventRowWithDetail({
                eventRow: evt,
                detail: null,
                unreadCount: unreadMap[evt.event_uuid] || 0,
              });
            }
          })
        );

        const rowKeyOf = (row) =>
          row?.search_hit_message_id
            ? `${row.event_uuid}-${row.search_hit_message_id}`
            : String(row?.event_uuid || '');

        if (append) {
          setEvents((prev) => {
            const seen = new Set(prev.map(rowKeyOf));
            const additions = patched.filter((row) => !seen.has(rowKeyOf(row)));
            return [...prev, ...additions];
          });
        } else {
          // リセット時は通常一覧のページング状態を止め、検索専用のページングに切り替える
          applySearchResultEvents({
            resultEvents: patched,
            setEvents,
            setHasMoreEvents,
            setEventsOffset,
          });
        }
        setSearchHasMore(nextHasMore);
        setSearchNextOffset(nextOffset);
        if (snapshotToken) setSearchSnapshotToken(snapshotToken);
        dlog('performSearch -> results', patched.length, { append, nextHasMore });
        if (announceResult) {
          info(`${patched.length}件${nextHasMore ? '以上' : ''}ヒットしました`);
        }
        return true;
      } catch (err) {
        dlog('performSearch error', err);
        const message = err?.message || 'イベント検索に失敗しました';
        setSearchModalError(message);
        toastError(message);
        return false;
      } finally {
        if (!append) setIsSearching(false);
        refreshEventChatUnread();
      }
    }, [
      clearSearch,
      getEventDetail,
      info,
      isSearchMode,
      messageSearchQuery,
      refreshEventChatUnread,
      searchEndDate,
      searchNextOffset,
      searchQuery,
      searchSnapshotToken,
      searchStartDate,
      toastError,
    ]);

    const loadMoreSearchResults = useCallback(async () => {
      if (!isTargetFilterMode || !searchHasMore || isSearchLoadingMore || isSearching) return;
      setIsSearchLoadingMore(true);
      try {
        await performSearch(null, { announceResult: false, append: true });
      } finally {
        setIsSearchLoadingMore(false);
      }
    }, [isTargetFilterMode, searchHasMore, isSearchLoadingMore, isSearching, performSearch]);

    const applyToolbarSearchFilters = useCallback(async (override = {}) => {
      const criteria = resolveEventSearchCriteria({
        override,
        eventQuery: searchQuery,
        messageQuery: messageSearchQuery,
        startDate: searchStartDate,
        endDate: searchEndDate,
      });
      setSearchModalError('');
      setSearchQuery(criteria.q);
      setMessageSearchQuery(criteria.message);
      setSearchStartDate(criteria.startDate);
      setSearchEndDate(criteria.endDate);
      if (!criteria.hasAny) {
        setIsSearchMode(false);
        setHasMoreEvents(true);
        setEventsOffset(0);
        if (isSearchMode) {
          await fetchEvents('reset');
        }
        return true;
      }
      return performSearch(
        {
          eventQuery: criteria.q,
          messageQuery: criteria.message,
          startDate: criteria.startDate,
          endDate: criteria.endDate,
        },
        { announceResult: false }
      );
    }, [fetchEvents, isSearchMode, messageSearchQuery, performSearch, searchEndDate, searchQuery, searchStartDate]);

    const searchToolbarChips = useMemo(
      () =>
        buildToolbarFilterChips(
          [
            {
              key: 'eventQuery',
              label: 'イベント',
              value: searchQuery,
              onRemove: () => applyToolbarSearchFilters({ eventQuery: '' }),
            },
            {
              key: 'messageQuery',
              label: '本文',
              value: messageSearchQuery,
              onRemove: () => applyToolbarSearchFilters({ messageQuery: '' }),
            },
            {
              key: 'startDate',
              label: '開始日',
              value: formatSearchChipDate(searchStartDate),
              onRemove: () => applyToolbarSearchFilters({ startDate: '' }),
            },
            {
              key: 'endDate',
              label: '終了日',
              value: formatSearchChipDate(searchEndDate),
              onRemove: () => applyToolbarSearchFilters({ endDate: '' }),
            },
            {
              key: 'assignee',
              label: '担当',
              value: assigneeSearch === 'mine' ? '自分' : '',
              onRemove: () => setAssigneeSearch(''),
            },
          ],
          { onClearAll: clearSearch }
        ),
      [applyToolbarSearchFilters, assigneeSearch, clearSearch, messageSearchQuery, searchEndDate, searchQuery, searchStartDate]
    );

    const applyTargetSearchModal = useCallback(async () => {
      await applyEventTargetSearchDraft({
        searchDraft,
        setSearchQuery,
        setMessageSearchQuery,
        setSearchStartDate,
        setSearchEndDate,
        performSearch,
        setShowSearchModal,
        setErrorMessage: setSearchModalError,
      });
    }, [performSearch, searchDraft]);

    /* 先頭のみ再取得（凍結中は抑止） */
    const requestRefreshTop = useCallback(() => {
      scheduleEventListRefreshTop({
        isSearchMode: isTargetFilterMode,
        orderFrozen,
        isBootLoading,
        refreshTopTimerRef,
        fetchEvents,
        delayMs: 120,
      });
    }, [fetchEvents, isBootLoading, isTargetFilterMode, orderFrozen]);

    const handleLoadMoreEvents = useCallback(async ({ force = false, reason = 'default' } = {}) => {
      if (eventListLoadMorePendingRef.current && !isLoadingEvents) {
        eventListLoadMorePendingRef.current = false;
      }
      if (eventListLoadMorePendingRef.current) return;
      eventListLoadMorePendingRef.current = true;
      try {
        const canLoadByState = shouldLoadMoreEventList({
          isSearchMode: isTargetFilterMode,
          isBootLoading,
          isLoadingEvents,
          hasMoreEvents,
        });
        const canLoadMore = force
          ? !isTargetFilterMode && !isBootLoading && !isLoadingEvents
          : canLoadByState;
        const loadedCount = await loadMoreEventList({
          canLoadMore,
          fetchEvents,
          pageLimit: EVENTS_PAGE_LIMIT,
          setHasMoreEvents,
        });
        if (loadedCount >= EVENTS_PAGE_LIMIT) {
          eventListForceFallbackExhaustedRef.current = false;
        } else if (force) {
          eventListForceFallbackExhaustedRef.current = true;
        }
      } finally {
        eventListLoadMorePendingRef.current = false;
      }
    }, [
      fetchEvents,
      hasMoreEvents,
      isBootLoading,
      isLoadingEvents,
      isTargetFilterMode,
    ]);

    const handleEventListScroll = useCallback((event) => {
      const node = event?.currentTarget || eventListNode;
      const remaining = node
        ? node.scrollHeight - node.clientHeight - node.scrollTop
        : Number.NaN;
      // 検索モードは専用のページング（50件ずつ・過去分を追加）で下端到達時に追加取得する
      if (isTargetFilterMode) {
        if (Number.isFinite(remaining) && remaining <= 200 && searchHasMore && !isSearchLoadingMore && !isSearching) {
          loadMoreSearchResults();
        }
        return;
      }
      const canLoadByState = shouldLoadMoreEventList({
        isSearchMode: isTargetFilterMode,
        isBootLoading,
        isLoadingEvents,
        hasMoreEvents,
      });
      const shouldForceFallback =
        remaining <= 180 &&
        !canLoadByState &&
        !isTargetFilterMode &&
        !isBootLoading &&
        !isLoadingEvents &&
        !eventListForceFallbackExhaustedRef.current &&
        displayedEvents.length >= EVENTS_PAGE_LIMIT;
      if (shouldForceFallback) {
        handleLoadMoreEvents({ force: true, reason: 'scroll-bottom-fallback' });
        return;
      }
      triggerEventListLoadMoreIfNeeded({
        container: event?.currentTarget || eventListNode,
        shouldLoad: () =>
          shouldLoadMoreEventList({
            isSearchMode: isTargetFilterMode,
            isBootLoading,
            isLoadingEvents,
            hasMoreEvents,
          }),
        onLoadMore: handleLoadMoreEvents,
        thresholdPx: 180,
      });
    }, [
      displayedEvents.length,
      eventListNode,
      handleLoadMoreEvents,
      hasMoreEvents,
      isBootLoading,
      isLoadingEvents,
      isTargetFilterMode,
      searchHasMore,
      isSearchLoadingMore,
      isSearching,
      loadMoreSearchResults,
    ]);

    useEffect(() => {
      return observeEventListLoadMoreSentinel({
        sentinel: loadMoreNode,
        root: eventListNode,
        shouldLoad: () =>
          shouldLoadMoreEventList({
            isSearchMode: isTargetFilterMode,
            isBootLoading,
            isLoadingEvents,
            hasMoreEvents,
          }),
        onLoadMore: handleLoadMoreEvents,
        rootMargin: '200px 0px',
        threshold: 0,
      });
    }, [eventListNode, handleLoadMoreEvents, hasMoreEvents, isBootLoading, isLoadingEvents, isTargetFilterMode, loadMoreNode]);

    useEffect(() => {
      return observeEventListLoadMoreScroll({
        container: eventListNode,
        shouldLoad: () =>
          shouldLoadMoreEventList({
            isSearchMode: isTargetFilterMode,
            isBootLoading,
            isLoadingEvents,
            hasMoreEvents,
          }),
        onLoadMore: handleLoadMoreEvents,
        thresholdPx: 180,
      });
    }, [eventListNode, handleLoadMoreEvents, hasMoreEvents, isBootLoading, isLoadingEvents, isTargetFilterMode]);

    useEffect(() => {
      if (!eventListNode || isTargetFilterMode) return undefined;
      const rafId =
        typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
          ? window.requestAnimationFrame(() => {
              triggerEventListLoadMoreIfNeeded({
                container: eventListNode,
                shouldLoad: () =>
                  shouldLoadMoreEventList({
                    isSearchMode: isTargetFilterMode,
                    isBootLoading,
                    isLoadingEvents,
                    hasMoreEvents,
                  }),
                onLoadMore: handleLoadMoreEvents,
                thresholdPx: 180,
              });
            })
          : null;
      return () => {
        if (rafId != null && typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
          window.cancelAnimationFrame(rafId);
        }
      };
    }, [displayedEvents.length, eventListNode, handleLoadMoreEvents, hasMoreEvents, isBootLoading, isLoadingEvents, isTargetFilterMode]);
  return {
    searchSummaryLabel,
    assigneeQuickToggles,
    displayedEvents,
    openTargetSearchModal,
    clearSearch,
    searchToolbarChips,
    applyTargetSearchModal,
    requestRefreshTop,
    isTargetFilterMode,
    handleEventListScroll,
  };
}